import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ArrowLeft, Upload, Plus, X, Save, Eye } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../Header';
import Footer from '../Footer';

const industries = [
  'Manufacturing & Heavy Industry',
  'Oil & Gas / Petrochemical',
  'Power Generation / Energy',
  'Water Treatment & Wastewater',
  'Marine',
  'Mining & Quarrying',
  'Buildings & Structures',
  'Food & Beverage',
  'Pulp & Paper',
  'Other'
];

const applicationTypes = [
  'Equipment Repair & Restoration',
  'Protective Coatings & Corrosion Prevention',
  'Pipeline Repair & Sealing',
  'Structural Repair & Reinforcement',
  'HVAC / Chiller / Cooling Tower Repair',
  'Industrial Cleaning & Media-Blasting',
  'Emergency In-Situ Repairs',
  'Tank Lining & Repair',
  'Pump & Valve Repair',
  'Heat Exchanger Repair',
  'Other'
];

// Complete Belzona product list
const belzonaProducts = [
  // 1000 Series - Metal Repair and Rebuilding
  { category: 'Metal Repair (1000 Series)', products: [
    'Belzona 1111 (Super Metal)',
    'Belzona 1121 (Super XL-Metal)',
    'Belzona 1131 (Bearing Metal)',
    'Belzona 1151 (Smoothing Metal)',
    'Belzona 1161 (Super UW-Metal)',
    'Belzona 1212',
    'Belzona 1221 (Super E-Metal)',
    'Belzona 1251 (HA-Metal)',
  ]},
  // 1300 Series - Erosion-Corrosion Protection
  { category: 'Erosion-Corrosion (1300 Series)', products: [
    'Belzona 1311 (Ceramic R-Metal)',
    'Belzona 1321 (Ceramic S-Metal)',
    'Belzona 1331',
    'Belzona 1341 (Supermetalglide)',
    'Belzona 1381',
    'Belzona 1391S',
    'Belzona 1391T',
    'Belzona 1392 (Ceramic HT2)',
  ]},
  // 1500 Series - High Temperature
  { category: 'High Temperature (1500 Series)', products: [
    'Belzona 1511 (Super HT-Metal)',
    'Belzona 1523',
    'Belzona 1593',
  ]},
  // 1600 Series - Underwater
  { category: 'Underwater (1600 Series)', products: [
    'Belzona 1611',
  ]},
  // 1800 Series - Abrasion Resistant
  { category: 'Abrasion Resistant (1800 Series)', products: [
    'Belzona 1811',
    'Belzona 1813',
    'Belzona 1814',
    'Belzona 1818',
  ]},
  // 2000 Series - Elastomers
  { category: 'Elastomers (2000 Series)', products: [
    'Belzona 2111 (D&A Hi-Build Elastomer)',
    'Belzona 2131 (D&A Fluid Elastomer)',
    'Belzona 2141 (ACR Fluid Elastomer)',
    'Belzona 2211 (MP Hi-Build Elastomer)',
    'Belzona 2311 (SR Elastomer)',
  ]},
  // 3000 Series - Membranes
  { category: 'Membranes (3000 Series)', products: [
    'Belzona 3111 (Flexible Membrane)',
    'Belzona 3131 (WG Membrane)',
    'Belzona 3211 (Lagseal)',
  ]},
  // 4000 Series - Concrete Repair
  { category: 'Concrete Repair (4000 Series)', products: [
    'Belzona 4111 (Magma-Quartz)',
    'Belzona 4131 (Magma-Screed)',
    'Belzona 4141 (Magma-Build)',
    'Belzona 4311 (Magma CR1)',
    'Belzona 4341 (Magma CR4)',
    'Belzona 4361',
  ]},
  // 5000 Series - Coatings
  { category: 'Protective Coatings (5000 Series)', products: [
    'Belzona 5111 (Ceramic Cladding)',
    'Belzona 5811 (Immersion Grade)',
    'Belzona 5812DW',
    'Belzona 5821',
    'Belzona 5831 (ST-Barrier)',
    'Belzona 5831LT',
    'Belzona 5841',
    'Belzona 5851 (HA-Barrier)',
    'Belzona 5892',
  ]},
  // Other
  { category: 'Other', products: [
    'SuperWrap Composite System',
    'Belzona 9611',
  ]},
];

// Flatten for simple dropdown or get all products
const getAllProducts = () => belzonaProducts.flatMap(cat => cat.products);

const CreateCaseStudy = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    industry: '',
    applicationType: '',
    products: [],
    problemDescription: '',
    solutionApplied: '',
    results: '',
    dateCompleted: '',
    customerName: '',
    location: '',
    beforeImages: [],
    afterImages: []
  });
  const [selectedProduct, setSelectedProduct] = useState('');
  const [customProduct, setCustomProduct] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${API_URL}/api/auth/me`, {
          credentials: 'include',
        });
        if (!response.ok) {
          navigate('/');
          return;
        }
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Auth check failed:', error);
        navigate('/');
      }
    };
    checkAuth();
  }, [navigate]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addProduct = () => {
    if (showCustomInput && customProduct.trim()) {
      if (!formData.products.includes(customProduct.trim())) {
        setFormData(prev => ({
          ...prev,
          products: [...prev.products, customProduct.trim()]
        }));
      }
      setCustomProduct('');
      setShowCustomInput(false);
    } else if (selectedProduct === 'custom') {
      setShowCustomInput(true);
      setSelectedProduct('');
    } else if (selectedProduct && !formData.products.includes(selectedProduct)) {
      setFormData(prev => ({
        ...prev,
        products: [...prev.products, selectedProduct]
      }));
      setSelectedProduct('');
    }
  };

  const addCustomProduct = () => {
    if (customProduct.trim() && !formData.products.includes(customProduct.trim())) {
      setFormData(prev => ({
        ...prev,
        products: [...prev.products, customProduct.trim()]
      }));
      setCustomProduct('');
      setShowCustomInput(false);
    }
  };

  const removeProduct = (product) => {
    setFormData(prev => ({
      ...prev,
      products: prev.products.filter(p => p !== product)
    }));
  };

  const handleImageUpload = async (type, e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    setUploadingImages(true);
    toast.info(`Uploading ${files.length} image(s)...`);
    
    // Upload each file to the backend and get permanent URLs
    const uploadPromises = files.map(async (file) => {
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        const response = await fetch(`${API_URL}/api/upload`, {
          method: 'POST',
          credentials: 'include',
          body: formData
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log('Upload success:', data);
          // Convert relative URL to absolute URL using current origin
          const fullUrl = data.url.startsWith('/') 
            ? `${window.location.origin}${data.url}` 
            : data.url;
          return fullUrl;
        } else {
          const errText = await response.text();
          console.error('Upload failed:', response.status, errText);
          return null;
        }
      } catch (error) {
        console.error('Upload error:', error);
        return null;
      }
    });
    
    try {
      const uploadedUrls = await Promise.all(uploadPromises);
      const validUrls = uploadedUrls.filter(url => url !== null);
      
      if (validUrls.length > 0) {
        setFormData(prev => ({
          ...prev,
          [type]: [...prev[type], ...validUrls]
        }));
        toast.success(`${validUrls.length} image(s) uploaded successfully!`);
      } else {
        toast.error('Failed to upload images. Please try again.');
      }
    } finally {
      setUploadingImages(false);
      // Reset the file input so the same file can be selected again
      e.target.value = '';
    }
  };

  const removeImage = (type, index) => {
    setFormData(prev => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.title || !formData.industry || !formData.applicationType || !formData.problemDescription || !formData.solutionApplied) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/case-studies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          status: 'Published'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create case study');
      }

      toast.success('Case study created successfully!');
      navigate('/customer-portal/case-studies');
    } catch (error) {
      console.error('Error creating case study:', error);
      toast.error('Failed to create case study. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    setLoading(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/case-studies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          status: 'Draft'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save draft');
      }

      toast.success('Draft saved successfully!');
      navigate('/customer-portal/case-studies');
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error('Failed to save draft. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      {/* Page Header */}
      <div className="pt-32 pb-8 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-4xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/customer-portal/case-studies')}
            className="mb-4 text-gray-600 hover:text-gray-900"
            data-testid="back-to-case-studies-btn"
          >
            <ArrowLeft size={18} className="mr-2" />
            Back to Case Studies
          </Button>
          <h1 className="text-4xl font-bold text-slate-900 mb-2">
            Create Case Study
          </h1>
          <p className="text-xl text-gray-600">
            Document your repair application project
          </p>
        </div>
      </div>

      {/* Form Content */}
      <div className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <form onSubmit={handleSubmit}>
            <div className="space-y-6">
              
              {/* Basic Information */}
              <Card>
                <CardHeader>
                  <CardTitle>Project Information</CardTitle>
                  <CardDescription>Basic details about the repair application</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="title">Project Title *</Label>
                    <Input
                      id="title"
                      placeholder="e.g., Cooling Tower Rehabilitation - Chemical Plant"
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      className="mt-1"
                      data-testid="case-study-title-input"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="industry">Industry *</Label>
                      <Select
                        value={formData.industry}
                        onValueChange={(value) => handleInputChange('industry', value)}
                      >
                        <SelectTrigger className="mt-1" data-testid="industry-select">
                          <SelectValue placeholder="Select industry" />
                        </SelectTrigger>
                        <SelectContent>
                          {industries.map((industry) => (
                            <SelectItem key={industry} value={industry}>
                              {industry}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="applicationType">Application Type *</Label>
                      <Select
                        value={formData.applicationType}
                        onValueChange={(value) => handleInputChange('applicationType', value)}
                      >
                        <SelectTrigger className="mt-1" data-testid="application-type-select">
                          <SelectValue placeholder="Select application type" />
                        </SelectTrigger>
                        <SelectContent>
                          {applicationTypes.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="dateCompleted">Date Completed</Label>
                      <Input
                        id="dateCompleted"
                        type="date"
                        value={formData.dateCompleted}
                        onChange={(e) => handleInputChange('dateCompleted', e.target.value)}
                        className="mt-1"
                        data-testid="date-completed-input"
                      />
                    </div>

                    <div>
                      <Label htmlFor="location">Location</Label>
                      <Input
                        id="location"
                        placeholder="e.g., Houston, TX"
                        value={formData.location}
                        onChange={(e) => handleInputChange('location', e.target.value)}
                        className="mt-1"
                        data-testid="location-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="customerName">Customer/Company Name (Optional)</Label>
                    <Input
                      id="customerName"
                      placeholder="e.g., ABC Manufacturing Inc."
                      value={formData.customerName}
                      onChange={(e) => handleInputChange('customerName', e.target.value)}
                      className="mt-1"
                      data-testid="customer-name-input"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Products Used */}
              <Card>
                <CardHeader>
                  <CardTitle>Products & Materials Used</CardTitle>
                  <CardDescription>Select Belzona products used in this project, or add custom products</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex gap-2">
                    <Select value={selectedProduct} onValueChange={setSelectedProduct}>
                      <SelectTrigger className="flex-1" data-testid="product-select">
                        <SelectValue placeholder="Select a Belzona product" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        <SelectItem value="custom" className="font-semibold text-blue-700">
                          + Add Custom Product (Not Listed)
                        </SelectItem>
                        {belzonaProducts.map((category) => (
                          <React.Fragment key={category.category}>
                            <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 bg-gray-100">
                              {category.category}
                            </div>
                            {category.products.map((product) => (
                              <SelectItem key={product} value={product}>
                                {product}
                              </SelectItem>
                            ))}
                          </React.Fragment>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button type="button" onClick={addProduct} data-testid="add-product-btn">
                      <Plus size={18} />
                    </Button>
                  </div>

                  {/* Custom Product Input */}
                  {showCustomInput && (
                    <div className="flex gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <Input
                        placeholder="Enter custom product name..."
                        value={customProduct}
                        onChange={(e) => setCustomProduct(e.target.value)}
                        className="flex-1"
                        data-testid="custom-product-input"
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomProduct())}
                      />
                      <Button type="button" onClick={addCustomProduct} data-testid="add-custom-product-btn">
                        Add
                      </Button>
                      <Button type="button" variant="outline" onClick={() => setShowCustomInput(false)}>
                        Cancel
                      </Button>
                    </div>
                  )}

                  {formData.products.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {formData.products.map((product, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-2 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
                        >
                          {product}
                          <button
                            type="button"
                            onClick={() => removeProduct(product)}
                            className="hover:text-blue-900"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Problem & Solution */}
              <Card>
                <CardHeader>
                  <CardTitle>Problem & Solution</CardTitle>
                  <CardDescription>Describe the issue and how it was resolved</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="problemDescription">Problem Description *</Label>
                    <Textarea
                      id="problemDescription"
                      placeholder="Describe the issue that needed to be addressed (e.g., severe corrosion, erosion damage, structural cracks, etc.)"
                      value={formData.problemDescription}
                      onChange={(e) => handleInputChange('problemDescription', e.target.value)}
                      className="mt-1 min-h-[120px]"
                      data-testid="problem-description-textarea"
                    />
                  </div>

                  <div>
                    <Label htmlFor="solutionApplied">Solution Applied *</Label>
                    <Textarea
                      id="solutionApplied"
                      placeholder="Describe the repair process and materials applied (e.g., surface preparation, application method, curing time, etc.)"
                      value={formData.solutionApplied}
                      onChange={(e) => handleInputChange('solutionApplied', e.target.value)}
                      className="mt-1 min-h-[120px]"
                      data-testid="solution-applied-textarea"
                    />
                  </div>

                  <div>
                    <Label htmlFor="results">Results & Outcome</Label>
                    <Textarea
                      id="results"
                      placeholder="Describe the measurable results (e.g., extended equipment life, cost savings, improved efficiency, etc.)"
                      value={formData.results}
                      onChange={(e) => handleInputChange('results', e.target.value)}
                      className="mt-1 min-h-[100px]"
                      data-testid="results-textarea"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Images */}
              <Card>
                <CardHeader>
                  <CardTitle>Before & After Images</CardTitle>
                  <CardDescription>Upload photos documenting the repair (optional)</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {uploadingImages && (
                    <div className="bg-blue-50 text-blue-700 p-3 rounded-lg flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-700"></div>
                      Uploading images...
                    </div>
                  )}
                  
                  {/* Before Images */}
                  <div>
                    <Label className="mb-2 block">Before Images</Label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        disabled={uploadingImages}
                        onChange={(e) => handleImageUpload('beforeImages', e)}
                        className="hidden"
                        id="beforeImages"
                      />
                      <label
                        htmlFor="beforeImages"
                        className={`flex flex-col items-center justify-center cursor-pointer py-4 ${uploadingImages ? 'opacity-50' : ''}`}
                      >
                        <Upload className="text-gray-400 mb-2" size={32} />
                        <span className="text-sm text-gray-600">Click to upload before images</span>
                      </label>
                      
                      {formData.beforeImages.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-4">
                          {formData.beforeImages.map((img, index) => (
                            <div key={index} className="relative">
                              <img
                                src={img}
                                alt={`Before ${index + 1}`}
                                className="w-24 h-24 object-contain rounded bg-gray-100"
                              />
                              <button
                                type="button"
                                onClick={() => removeImage('beforeImages', index)}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                              >
                                <X size={12} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* After Images */}
                  <div>
                    <Label className="mb-2 block">After Images</Label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        disabled={uploadingImages}
                        onChange={(e) => handleImageUpload('afterImages', e)}
                        className="hidden"
                        id="afterImages"
                      />
                      <label
                        htmlFor="afterImages"
                        className={`flex flex-col items-center justify-center cursor-pointer py-4 ${uploadingImages ? 'opacity-50' : ''}`}
                      >
                        <Upload className="text-gray-400 mb-2" size={32} />
                        <span className="text-sm text-gray-600">Click to upload after images</span>
                      </label>
                      
                      {formData.afterImages.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-4">
                          {formData.afterImages.map((img, index) => (
                            <div key={index} className="relative">
                              <img
                                src={img}
                                alt={`After ${index + 1}`}
                                className="w-24 h-24 object-contain rounded bg-gray-100"
                              />
                              <button
                                type="button"
                                onClick={() => removeImage('afterImages', index)}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                              >
                                <X size={12} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleSaveDraft}
                  disabled={loading}
                  className="flex items-center gap-2"
                  data-testid="save-draft-btn"
                >
                  <Save size={18} />
                  {loading ? 'Saving...' : 'Save as Draft'}
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-blue-900 hover:bg-blue-800 flex items-center gap-2"
                  data-testid="publish-case-study-btn"
                >
                  <Eye size={18} />
                  {loading ? 'Publishing...' : 'Publish Case Study'}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CreateCaseStudy;
